/**
 * The SPA reads its tenant + client IDs from `/runtime-config.json`, served
 * by the Container App from the same origin. This lets one image be
 * configured per-environment without rebuilding.
 *
 * In dev, Vite reads .env.development with the same keys.
 */

export type RuntimeConfig = {
  tenantId: string;
  spaClientId: string;
  apiScope: string;
};

let cached: RuntimeConfig | null = null;

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (cached) return cached;

  // In dev we use VITE_ env vars; in prod we fetch a JSON file written into
  // the static build at deploy time (or served by the API on first request).
  if (import.meta.env.DEV) {
    cached = {
      tenantId: import.meta.env.VITE_ENTRA_TENANT_ID ?? "",
      spaClientId: import.meta.env.VITE_ENTRA_SPA_CLIENT_ID ?? "",
      apiScope: import.meta.env.VITE_ENTRA_API_SCOPE ?? "",
    };
    if (!cached.tenantId || !cached.spaClientId || !cached.apiScope) {
      throw new Error(
        "Set VITE_ENTRA_TENANT_ID, VITE_ENTRA_SPA_CLIENT_ID, VITE_ENTRA_API_SCOPE in apps/web/.env.development",
      );
    }
    return cached;
  }

  const res = await fetch("/runtime-config.json", { cache: "no-store" });
  if (!res.ok) throw new Error(`Failed to load runtime config: ${res.status}`);
  cached = (await res.json()) as RuntimeConfig;
  return cached;
}
